// externals
import { RSAA } from 'redux-api-middleware';
import type { Dispatch } from 'redux';

// libraries
import type { RSAACallAction, RSAACallRequest } from '@makemydeal/dr-react-18-wrappers';
import { injectMetadataOnRSAA, scopedActions } from '@makemydeal/shared-scoped-store';

import type { VehicleAnalyticsPayload } from '../types/analyticsPayloadTypes';

import {
    CLICKED_BEGIN_CHECKOUT,
    CLICKED_CONTINUE_IN_DESKING,
    CLICKED_CONTINUE_IN_DESKING_CONFIRM,
    CLICKED_CONTINUE_IN_UNIFI,
    CLICKED_COPY_DEAL_URL,
    CLICKED_COPY_DEAL_URL_CLICKED,
    CLICKED_PUSH_TO_DMS_CONFIRM,
    CLICKED_SEND_TO_CUSTOMER,
    CLICKED_SEND_TO_ECOMMERCE,
    SEND_TO_SALES_VIEW_CONFIRM,
    SHOW_TOTAL_TAX_RATE_SUPERSEDE_ALERT,
    HIDE_TOTAL_TAX_RATE_SUPERSEDE_ALERT,
    SET_FORCE_RELOAD_ON_WS_EVENT_TRIGGERED,
    AI_LENDER_PROBABILITY_REQUEST,
    AI_LENDER_PROBABILITY_SUCCESS,
    AI_LENDER_PROBABILITY_FAILURE,
    UPDATE_QUALIFICATION_IDS,
    AI_VEHICLE_RECOMMENDATIONS_REQUEST,
    AI_VEHICLE_RECOMMENDATIONS_SUCCESS,
    AI_VEHICLE_RECOMMENDATIONS_FAILURE,
    UPDATE_VEHICLE_WITH_SELECTED_RECOMMENDATION,
    SHOW_AI_REFRESH_BUTTON,
    HIDE_AI_REFRESH_BUTTON,
    TRIGGER_AI_REFRESH,
    AI_REFRESH_CLICKED,
    VEHICLE_RECOMMENDATION_DISPLAYED,
    VEHICLE_RECOMMENDATION_SELECTED,
    SOFTPULL_CONSENT_CONTINUE_CLICKED,
    SOFTPULL_CONSENT_CANCEL_CLICKED,
    LENDER_PROBABILITY_DISPLAYED,
    LENDER_RETURN_TO_OFFER_CLICKED,
    LENDER_BACKEND_LIMIT_DETAILS_CLICKED,
    SOFTPULL_ALT_CONSENT_CONTINUE_CLICKED,
    SOFTPULL_MANDATORY_DETAILS_CONTINUE_CLICKED,
    ALT_DEAL_STRUCTURE_LINK_CLICKED,
    ALT_DEAL_STRUCTURE_OPTIONS_VIEWED,
    ALT_DEAL_STRUCTURE_CREATE_SCENARIO_CLICKED,
    ALT_DEAL_STRUCTURE_CANCEL_CLICKED,
    ALT_DEAL_STRUCTURE_TERM_DOWN_PAYMENT_UPDATED,
    ALT_DEAL_STRUCTURE_RETRY_RATES_CLICKED
} from '../actionTypes/deskingActionTypes';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { buildFromConfig } from '@makemydeal/dr-common-utils';
import * as configSelectors from '../selectors/config';
import * as offerReduxSelectors from '../selectors/offerRedux';
import type { QualificationId } from '@makemydeal/dr-shared-types';
import { getLenderProbabilitiesPayload } from '../utils/lenderProbabiltyUtils';
import { getSoftPullConsentProvided } from '../selectors/offerInfoSelectors';
import { buildOfferSavePayloadObject } from '../utils/offerPayloadUtil';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import type { VehicleRecommendationPayload, UnifiedAIPayload } from '../types/aiPayloadTypes';
import type { LenderProbabilityPayload } from '../types/lenderProbabiltyTypes';
import type { CdlAltDealStructurePrediction } from '../types/cdl';

// Map to track abort controllers by dealXgId
const lenderProbabilityAbortControllers = new Map<string, AbortController>();
const vehicleRecommendationAbortControllers = new Map<string, AbortController>();

export const continueInDesking = () => ({
    type: CLICKED_CONTINUE_IN_DESKING
});

export const sendToEcommerce = () => ({
    type: CLICKED_SEND_TO_ECOMMERCE
});

export const copyDealUrl = () => ({
    type: CLICKED_COPY_DEAL_URL
});

export const copyDealUrlClicked = () => ({
    type: CLICKED_COPY_DEAL_URL_CLICKED
});

export const continueInUnifi = () => ({
    type: CLICKED_CONTINUE_IN_UNIFI
});

export const pushToDmsConfirmClicked = () => ({
    type: CLICKED_PUSH_TO_DMS_CONFIRM
});

export const sendToCustomerClicked = () => ({
    type: CLICKED_SEND_TO_CUSTOMER
});

export const beginCheckoutClicked = () => ({
    type: CLICKED_BEGIN_CHECKOUT
});

export const continueInDeskingConfirmClicked = () => ({
    type: CLICKED_CONTINUE_IN_DESKING_CONFIRM
});

export const shareToSalesViewConfirmClicked = () => ({
    type: SEND_TO_SALES_VIEW_CONFIRM
});

export const showTotalTaxRateSupersedeAlert = () => ({
    type: SHOW_TOTAL_TAX_RATE_SUPERSEDE_ALERT
});

export const hideTotalTaxRateSupersedeAlert = () => ({
    type: HIDE_TOTAL_TAX_RATE_SUPERSEDE_ALERT
});

export const setForceReloadOnWSEvent = (hasForceReloadOnWSEventTriggered: boolean, source?: string, message?: string) => ({
    type: SET_FORCE_RELOAD_ON_WS_EVENT_TRIGGERED,
    payload: { hasForceReloadOnWSEventTriggered, forceReloadSource: source, forceReloadMessage: message }
});

// #region: AI Vehicle Affordability Actions

export const fetchLenderProbabilityData = (
    dealStructureId?: string,
    scopeIdForRouting?: string
): ((dispatch: Dispatch<any, any>, getState: () => StateTree) => void) => {
    return (dispatch, getState) => {
        const state = getState();
        const bffEndpointConfig = configSelectors.getServicesBff(state);
        const dealXgId = offerReduxSelectors.getDealXgId(state);
        const softPullConsentProvided = getSoftPullConsentProvided(state);
        const creditScoreSource = offerReduxSelectors.getCreditScoreSource(state);

        const isUnsavedStateEnabled = featureToggleSelectors.enableUnsavedStateForAI(state);

        let payload: UnifiedAIPayload | LenderProbabilityPayload;
        let currentVin: string;

        if (isUnsavedStateEnabled) {
            const offerPayload = buildOfferSavePayloadObject(state);
            currentVin = offerPayload.vin || '';
            payload = {
                dealId: dealXgId,
                offer: offerPayload,
                softPullConsentProvided,
                creditScoreSource
            };

            if (dealStructureId) {
                // @ts-expect-error scenario should exist
                payload.offer = offerPayload.comparisonInfo?.alternateScenarios?.find(
                    (comp) => comp.meta?.scenarioId === dealStructureId
                );
                if (!payload.offer) {
                    // eslint-disable-next-line no-console
                    console.error(`Scenario with ID ${dealStructureId} not found in offer payload.`);
                    return;
                }
            }
        } else {
            // Legacy approach: Use proxy endpoint with detailed payload
            const lenderPayload = getLenderProbabilitiesPayload(state);
            currentVin = lenderPayload.vehicle.vin;
            payload = { ...lenderPayload, creditScoreSource };
        }

        // Use VIN-based abort key for lender probability to allow different vehicles to have separate requests
        // Format: dealXgId|VIN (or scopeId|VIN for multi-scenario)
        const baseKey = dealStructureId || scopeIdForRouting || dealXgId;
        const abortKey = currentVin ? `${baseKey}|${currentVin}` : baseKey;

        // Check if there's already an in-flight request for this key (duplicate prevention)
        if (abortKey && lenderProbabilityAbortControllers.has(abortKey)) {
            // Skip duplicate request - don't dispatch
            return;
        }

        // Abort previous request for this base key (different VIN) if it exists
        if (baseKey) {
            const controller = lenderProbabilityAbortControllers.get(baseKey);
            if (controller) {
                controller.abort();
                lenderProbabilityAbortControllers.delete(baseKey);
            }
        }

        // Create new abort controller for this key
        const abortController = new AbortController();
        if (abortKey) {
            lenderProbabilityAbortControllers.set(abortKey, abortController);
        }

        const bffBaseUrl = buildFromConfig(bffEndpointConfig, '');
        const endpoint = `${bffBaseUrl}/ai/lender-probability`;

        const request: RSAACallRequest = {
            endpoint,
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json'
            },
            body: JSON.stringify(payload),
            options: {
                signal: abortController.signal
            },
            types: [
                AI_LENDER_PROBABILITY_REQUEST,
                {
                    type: AI_LENDER_PROBABILITY_SUCCESS,
                    meta: () => {
                        // Clean up abort controller on success
                        if (abortKey) {
                            lenderProbabilityAbortControllers.delete(abortKey);
                        }
                    }
                },
                {
                    type: AI_LENDER_PROBABILITY_FAILURE,
                    meta: (action: RSAACallAction, _res: Response) => {
                        const options = action[RSAA].options as RequestInit | undefined;
                        const isAborted = options?.signal?.aborted || false;

                        // Clean up abort controller on failure
                        if (abortKey && !isAborted) {
                            lenderProbabilityAbortControllers.delete(abortKey);
                        }

                        return { isAborted };
                    }
                }
            ]
        };

        let rsaaAction: RSAACallAction = {
            [RSAA]: request
        } as RSAACallAction;

        // If scopeIdForRouting is provided, inject scope metadata so the SUCCESS action
        // is dispatched to the correct scenario's scope
        if (scopeIdForRouting) {
            rsaaAction = injectMetadataOnRSAA(rsaaAction, scopedActions.createScopedActionMeta(scopeIdForRouting));
        }

        dispatch(rsaaAction);
    };
};

export const updateQualificationIds = (qualificationIds: Array<QualificationId>) => ({
    type: UPDATE_QUALIFICATION_IDS,
    payload: qualificationIds
});

export const fetchVehicleRecommendations = (
    dealStructureId?: string,
    scopeIdForRouting?: string
): ((dispatch: Dispatch<any, any>, getState: () => StateTree) => void) => {
    return (dispatch, getState) => {
        const state = getState();
        const bffEndpointConfig = configSelectors.getServicesBff(state);
        const dealXgId = offerReduxSelectors.getDealXgId(state);
        const softPullConsentProvided = getSoftPullConsentProvided(state);
        const creditScoreSource = offerReduxSelectors.getCreditScoreSource(state);

        // Key the abort controller per scenario/scope so concurrent per-scenario fetches don't abort
        // each other; fall back to dealXgId for the primary path. Deliberately keyed off the seed
        // (dealStructureId), not scopeIdForRouting: the primary path routes explicitly to its scope but
        // must keep the dealXgId key so it still mutually-cancels aiMiddleware's primary fetch.
        const abortKey = dealStructureId || dealXgId;

        if (abortKey) {
            const controller = vehicleRecommendationAbortControllers.get(abortKey);
            if (controller) {
                controller.abort();
                vehicleRecommendationAbortControllers.delete(abortKey);
            }
        }

        const abortController = new AbortController();
        if (abortKey) {
            vehicleRecommendationAbortControllers.set(abortKey, abortController);
        }

        const bffBaseUrl = buildFromConfig(bffEndpointConfig, '');
        const endpoint = `${bffBaseUrl}/ai/vehicle-recommendation`;

        const isUnsavedStateEnabled = featureToggleSelectors.enableUnsavedStateForAI(state);

        let payload: UnifiedAIPayload | VehicleRecommendationPayload;

        if (isUnsavedStateEnabled) {
            const offerPayload = buildOfferSavePayloadObject(state);
            payload = {
                dealId: dealXgId,
                offer: offerPayload,
                softPullConsentProvided,
                creditScoreSource
            };

            // Seed off the requested scenario's alternate offer instead of the primary (mirrors
            // fetchLenderProbabilityData); buildOfferSavePayloadObject nests alternates under comparisonInfo.
            if (dealStructureId) {
                // @ts-expect-error alternate scenario is the per-scope offer shape
                payload.offer = offerPayload.comparisonInfo?.alternateScenarios?.find(
                    (comp) => comp.meta?.scenarioId === dealStructureId
                );
                if (!payload.offer) {
                    // eslint-disable-next-line no-console
                    console.error(`Scenario with ID ${dealStructureId} not found in offer payload.`);
                    return;
                }
            }
        } else {
            payload = {
                dealId: dealXgId,
                softPullConsentProvided,
                creditScoreSource
            };
        }

        const request: RSAACallRequest = {
            endpoint,
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json'
            },
            body: JSON.stringify(payload),
            options: {
                signal: abortController.signal
            },
            types: [
                AI_VEHICLE_RECOMMENDATIONS_REQUEST,
                {
                    type: AI_VEHICLE_RECOMMENDATIONS_SUCCESS,
                    meta: () => {
                        // Clean up abort controller on success
                        if (abortKey) {
                            vehicleRecommendationAbortControllers.delete(abortKey);
                        }
                    }
                },
                {
                    type: AI_VEHICLE_RECOMMENDATIONS_FAILURE,
                    meta: (action: RSAACallAction, _res: Response) => {
                        const options = action[RSAA].options as RequestInit | undefined;
                        const isAborted = options?.signal?.aborted || false;

                        // Clean up abort controller on failure
                        if (abortKey && !isAborted) {
                            vehicleRecommendationAbortControllers.delete(abortKey);
                        }

                        return { isAborted };
                    }
                }
            ]
        };

        let rsaaAction: RSAACallAction = { [RSAA]: request } as RSAACallAction;

        // Route the result into the requested scenario's scope (mirrors fetchLenderProbabilityData).
        if (scopeIdForRouting) {
            rsaaAction = injectMetadataOnRSAA(rsaaAction, scopedActions.createScopedActionMeta(scopeIdForRouting));
        }

        dispatch(rsaaAction);
    };
};

export const updateVehicleWithSelectedRecommendation = (vehiclePayload: any) => ({
    type: UPDATE_VEHICLE_WITH_SELECTED_RECOMMENDATION,
    payload: vehiclePayload
});

// #endregion: AI Vehicle Affordability Actions

// #region: AI Refresh Button Actions

/**
 * Shows the AI refresh button to indicate that AI recommendations
 * may be stale due to unsaved changes
 */
export const showAIRefreshButton = () => ({
    type: SHOW_AI_REFRESH_BUTTON
});

/**
 * Clears the AI refresh button for a scope — e.g. a freshly-created scenario is not stale, so it should
 * not inherit the refresh flag copied from the scope it was cloned from.
 */
export const hideAIRefreshButton = () => ({
    type: HIDE_AI_REFRESH_BUTTON
});

/**
 * Triggers a refresh of all AI recommendations with current unsaved state.
 */
export const triggerAIRefresh = () => ({
    type: TRIGGER_AI_REFRESH
});

// #endregion: AI Refresh Button Actions

// #region: AI Analytics Action Creators (CDL3 Desking Intelligence)

export type { VehicleAnalyticsPayload };

export interface LenderAnalyticsPayload {
    lenderId: string;
    lenderName?: string;
    loanLikelihood: number;
    buyRate?: number;
    // ILS-powered backend limit, forwarded to dc-mv-278/279/321.
    backendLimit?: string | null;
}

export interface VehicleRecommendationAnalyticsPayload extends VehicleAnalyticsPayload {
    cadsVehicleCatalogId?: string | number;
    acceptanceLikelihood?: string;
    loanLikelihood?: string;
    lenderId?: string;
    lenderName?: string;
}

/**
 * dc-mv-260: Click refresh button to load AI indicators with terms changed
 */
export const aiRefreshClicked = (vehicleInfo?: VehicleAnalyticsPayload, lenderProbabilities?: LenderAnalyticsPayload[]) => ({
    type: AI_REFRESH_CLICKED,
    payload: { vehicleInfo, lenderProbabilities }
});

/**
 * dc-mv-261: Display alternate vehicle options (fire per vehicle)
 */
export const vehicleRecommendationDisplayed = (vehicleInfo: VehicleRecommendationAnalyticsPayload) => ({
    type: VEHICLE_RECOMMENDATION_DISPLAYED,
    payload: { vehicleInfo }
});

/**
 * dc-mv-285: Select vehicle from AI supported vehicle list
 */
export const vehicleRecommendationSelected = (vehicleInfo: VehicleRecommendationAnalyticsPayload) => ({
    type: VEHICLE_RECOMMENDATION_SELECTED,
    payload: { vehicleInfo }
});

/**
 * dc-mv-277: Click Continue on softpull consent modal
 */
export const softpullConsentContinueClicked = (vehicleInfo?: VehicleAnalyticsPayload) => ({
    type: SOFTPULL_CONSENT_CONTINUE_CLICKED,
    payload: { vehicleInfo }
});

/**
 * dc-mv-283: Click Cancel on softpull consent modal
 */
export const softpullConsentCancelClicked = (vehicleInfo?: VehicleAnalyticsPayload) => ({
    type: SOFTPULL_CONSENT_CANCEL_CLICKED,
    payload: { vehicleInfo }
});

/**
 * dc-mv-278: Display lender probability (fire per lender/likelihood pair)
 */
export const lenderProbabilityDisplayed = (lenderInfo: LenderAnalyticsPayload, vehicleInfo?: VehicleAnalyticsPayload) => ({
    type: LENDER_PROBABILITY_DISPLAYED,
    payload: { lenderInfo, vehicleInfo }
});

/**
 * dc-mv-279: Click Return to Offer CTA in lender slideout
 */
export const lenderReturnToOfferClicked = (
    vehicleInfo?: VehicleAnalyticsPayload,
    lenderProbabilities?: LenderAnalyticsPayload[]
) => ({
    type: LENDER_RETURN_TO_OFFER_CLICKED,
    payload: { vehicleInfo, lenderProbabilities }
});

/**
 * dc-mv-321: Click View Backend Limit Details link in lender comparison
 */
export const lenderBackendLimitDetailsClicked = (lenderInfo: LenderAnalyticsPayload, vehicleInfo?: VehicleAnalyticsPayload) => ({
    type: LENDER_BACKEND_LIMIT_DETAILS_CLICKED,
    payload: { lenderInfo, vehicleInfo }
});

/**
 * dc-mv-281: Click Continue on softpull slideout
 */
export const softpullAltConsentContinueClicked = (vehicleInfo?: VehicleAnalyticsPayload) => ({
    type: SOFTPULL_ALT_CONSENT_CONTINUE_CLICKED,
    payload: { vehicleInfo }
});

/**
 * dc-mv-300: Click Update Customer & Initiate Soft Pull on mandatory details card
 */
export const softpullMandatoryDetailsContinueClicked = (vehicleInfo?: VehicleAnalyticsPayload) => ({
    type: SOFTPULL_MANDATORY_DETAILS_CONTINUE_CLICKED,
    payload: { vehicleInfo }
});

// Tag payload shape is defined once in `types/cdl` (CdlAltDealStructurePrediction) and used directly here
// so the action creators and the CDL middleware can't silently drift.
export interface AltDealStructurePredictionActionPayload {
    vehicleInfo?: VehicleAnalyticsPayload;
    dealStructure?: CdlAltDealStructurePrediction[];
}

/**
 * dc-mv-288: View AI generated deal type structures (Alternative Deal Structure Link click)
 */
export const altDealStructureLinkClicked = (vehicleInfo?: VehicleAnalyticsPayload) => ({
    type: ALT_DEAL_STRUCTURE_LINK_CLICKED,
    payload: { vehicleInfo }
});

/**
 * dc-mv-289: View alternative deal structure options by deal type (fire once per deal type).
 * `dealStructure` = the presented structures for that deal type.
 */
export const altDealStructureOptionsViewed = (
    vehicleInfo: VehicleAnalyticsPayload | undefined,
    dealStructure: CdlAltDealStructurePrediction[]
) => ({
    type: ALT_DEAL_STRUCTURE_OPTIONS_VIEWED,
    payload: { vehicleInfo, dealStructure }
});

/**
 * dc-mv-290: Create deal scenario with AI supported deal structure information.
 * `dealStructure` = the selected scenarios (predicted structure per selected cell).
 */
export const altDealStructureCreateScenarioClicked = (payload: AltDealStructurePredictionActionPayload) => ({
    type: ALT_DEAL_STRUCTURE_CREATE_SCENARIO_CLICKED,
    payload
});

/**
 * dc-mv-291: Exit AI supported deal structure options without saving.
 * `dealStructure` = all presented deal structures.
 */
export const altDealStructureCancelClicked = (
    vehicleInfo?: VehicleAnalyticsPayload,
    dealStructure?: CdlAltDealStructurePrediction[]
) => ({
    type: ALT_DEAL_STRUCTURE_CANCEL_CLICKED,
    payload: { vehicleInfo, dealStructure }
});

/**
 * dc-mv-292: Editing AI proposed deal structure (term and/or down payment).
 * `dealStructure` = the presented structures for that deal type.
 */
export const altDealStructureTermDownPaymentUpdated = (
    vehicleInfo: VehicleAnalyticsPayload | undefined,
    dealStructure: CdlAltDealStructurePrediction[]
) => ({
    type: ALT_DEAL_STRUCTURE_TERM_DOWN_PAYMENT_UPDATED,
    payload: { vehicleInfo, dealStructure }
});

/**
 * dc-mv-296: Update deal structure overview (retry rates).
 * `dealStructure` = all presented deal structures.
 */
export const altDealStructureRetryRatesClicked = (
    vehicleInfo?: VehicleAnalyticsPayload,
    dealStructure?: CdlAltDealStructurePrediction[]
) => ({
    type: ALT_DEAL_STRUCTURE_RETRY_RATES_CLICKED,
    payload: { vehicleInfo, dealStructure }
});

// #endregion: AI Analytics Action Creators
